import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Checkbox, FormControlLabel, IconButton, TextField } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useMutation } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { submitPublicPrEnquiry } from "services/prEnquiryApi";
import {
  COURSE_LEVELS,
  ENGLISH_TESTS,
  MARITAL_STATUSES,
  PR_ENQUIRY_STEPS,
  PR_PATHWAYS,
  SKILLS_ASSESSMENT,
  STATES,
  TIMELINES,
  VISA_TYPES,
  YES_NO,
  buildPrEnquiryPayload,
  emptyExperience,
  experienceMonths,
  formatDuration,
  initialPrEnquiry,
  prEnquiryStepProblem,
  totalExperience,
} from "utils/prEnquiryForm";

const draftKey = (slug) => `kothar.prEnquiry.${slug}`;

const readDraft = (slug) => {
  try {
    const saved = JSON.parse(window.sessionStorage.getItem(draftKey(slug)) || "null");
    return saved ? { ...initialPrEnquiry(), ...saved, consent: false } : null;
  } catch {
    return null;
  }
};

const writeDraft = (slug, form) => {
  try {
    window.sessionStorage.setItem(draftKey(slug), JSON.stringify(form));
  } catch {
    // Private windows can refuse storage; the form still works without a draft.
  }
};

const clearDraft = (slug) => {
  try {
    window.sessionStorage.removeItem(draftKey(slug));
  } catch {
    // nothing to clear
  }
};

/** What went wrong with a submission, in words the person filling the form can act on. */
const submissionProblem = (error) => {
  const { status, data } = error?.response || {};
  switch (status) {
    case 400:
      return data?.errorMessage || data?.message || "Some answers need another look. Please check and try again.";
    case 404:
      return "This form link isn't valid. Please ask the office for a new link.";
    case 410:
      return "This form link is no longer accepting enquiries. Please ask the office for a new link.";
    case 429:
      return data?.errorMessage || "Too many submissions. Please wait a few minutes and try again.";
    default:
      return "We couldn't send your enquiry. Please check your connection and try again.";
  }
};

const Section = ({ title, hint, children }) => (
  <section className="space-y-4">
    <div>
      <h3 className="m-0 text-base font-semibold text-slate-900">{title}</h3>
      {hint ? <p className="m-0 mt-0.5 text-sm text-slate-500">{hint}</p> : null}
    </div>
    {children}
  </section>
);

const Grid = ({ children }) => <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{children}</div>;

const Text = ({ label, value, onChange, required, type = "text", multiline, placeholder, helperText }) => (
  <TextField
    label={label}
    value={value}
    onChange={(event) => onChange(event.target.value)}
    required={required}
    type={type}
    multiline={multiline}
    minRows={multiline ? 3 : undefined}
    placeholder={placeholder}
    helperText={helperText}
    size="small"
    fullWidth
    InputLabelProps={type === "date" || type === "month" ? { shrink: true } : undefined}
  />
);

/** Tap-to-pick options: one value, or several when `multiple`. */
const Choices = ({ label, options, value, onChange, multiple, required }) => {
  const selected = multiple ? value : [value];
  const toggle = (option) => {
    if (!multiple) return onChange(value === option ? "" : option);
    return onChange(selected.includes(option) ? selected.filter((v) => v !== option) : [...selected, option]);
  };
  return (
    <fieldset className="m-0 border-0 p-0">
      <legend className="mb-2 p-0 text-sm font-medium text-slate-700">
        {label}
        {required ? <span className="text-rose-500"> *</span> : null}
        {multiple ? <span className="ml-1 font-normal text-slate-400">(choose any)</span> : null}
      </legend>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const active = selected.includes(option);
          return (
            <button
              key={option}
              type="button"
              aria-pressed={active}
              onClick={() => toggle(option)}
              className={[
                "rounded-full border px-3.5 py-1.5 text-sm cursor-pointer transition-colors",
                active
                  ? "border-teal-600 bg-teal-600 text-white"
                  : "border-slate-200 bg-white text-slate-700 hover:border-teal-300 hover:bg-teal-50",
              ].join(" ")}
            >
              {option}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
};

const ExperienceList = ({ title, hint, entries, onChange, showHours }) => {
  const update = (index, changes) =>
    onChange(entries.map((entry, i) => (i === index ? { ...entry, ...changes } : entry)));

  return (
    <Section title={title} hint={hint}>
      {entries.length ? (
        <ul className="m-0 list-none space-y-3 p-0">
          {entries.map((entry, index) => (
            <li key={index} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Job {index + 1}
                  {experienceMonths(entry) ? (
                    <span className="ml-2 font-normal normal-case tracking-normal text-teal-700">
                      {formatDuration(experienceMonths(entry))}
                    </span>
                  ) : null}
                </span>
                <IconButton
                  size="small"
                  aria-label={`Remove job ${index + 1}`}
                  onClick={() => onChange(entries.filter((_, i) => i !== index))}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </div>
              <Grid>
                <Text label="Role / job title" required value={entry.role} onChange={(v) => update(index, { role: v })} />
                <Text label="Industry" value={entry.employer} onChange={(v) => update(index, { employer: v })} placeholder="e.g. Hospitality, IT, etc."/>
                <Text label="City" value={entry.location} onChange={(v) => update(index, { location: v })} placeholder="e.g. Sydney, Melbourne, etc."/>
             
                <Text
                  label="Started"
                  type="month"
                  required
                  placeholder="YYYY-MM"
                  value={entry.start}
                  onChange={(v) => update(index, { start: v })}
                />
                {entry.current ? (
                  <div className="flex items-center text-sm text-slate-500">Still working here</div>
                ) : (
                  <Text
                    label="Finished"
                    type="month"
                    placeholder="YYYY-MM"
                    value={entry.end}
                    onChange={(v) => update(index, { end: v })}
                  />
                )}
              </Grid>
              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Checkbox
                    size="small"
                    checked={entry.current}
                    onChange={(event) => update(index, { current: event.target.checked, end: "" })}
                  />
                }
                label={<span className="text-sm">I still work here</span>}
              />
            </li>
          ))}
        </ul>
      ) : (
        <p className="m-0 rounded-xl border border-dashed border-slate-200 px-4 py-3 text-sm text-slate-500">
          None added. That's fine if you have none.
        </p>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button
          size="small"
          startIcon={<AddCircleOutlineIcon />}
          onClick={() => onChange([...entries, emptyExperience()])}
        >
          Add a job
        </Button>
        {entries.length ? (
          <span className="text-sm text-slate-600">
            Total: <strong>{totalExperience(entries)}</strong>
          </span>
        ) : null}
      </div>
    </Section>
  );
};

const ReviewRow = ({ label, value }) =>
  value && (!Array.isArray(value) || value.length) ? (
    <div className="grid grid-cols-[9rem_1fr] gap-3 py-1.5 text-sm">
      <dt className="text-slate-500">{label}</dt>
      <dd className="m-0 text-slate-800">{Array.isArray(value) ? value.join(", ") : value}</dd>
    </div>
  ) : null;

export default function PrEnquiryPublicForm() {
  const { slug } = useParams();
  const [form, setForm] = useState(() => readDraft(slug) || initialPrEnquiry());
  const [stepIndex, setStepIndex] = useState(0);
  const [problem, setProblem] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const step = PR_ENQUIRY_STEPS[stepIndex];
  const isLast = stepIndex === PR_ENQUIRY_STEPS.length - 1;
  const progress = Math.round(((stepIndex + 1) / PR_ENQUIRY_STEPS.length) * 100);

  useEffect(() => {
    if (!submitted) writeDraft(slug, form);
  }, [slug, form, submitted]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [stepIndex]);

  const set = (field) => (value) => setForm((current) => ({ ...current, [field]: value }));

  const submit = useMutation({
    mutationFn: () => submitPublicPrEnquiry(slug, buildPrEnquiryPayload(form)),
    onSuccess: () => {
      clearDraft(slug);
      setSubmitted(true);
    },
    onError: (error) => setProblem(submissionProblem(error)),
  });

  const next = () => {
    const issue = prEnquiryStepProblem(step.id, form);
    setProblem(issue);
    if (issue) return;
    if (isLast) submit.mutate();
    else setStepIndex((index) => index + 1);
  };

  const back = () => {
    setProblem(null);
    setStepIndex((index) => Math.max(index - 1, 0));
  };

  const onshoreTotal = useMemo(() => totalExperience(form.onshore), [form.onshore]);
  const offshoreTotal = useMemo(() => totalExperience(form.offshore), [form.offshore]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-teal-50 to-emerald-100 px-4 py-8">
      <div className="mx-auto w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        <header className="bg-primary px-5 py-6 md:px-8 md:py-8">
          <div className="flex items-center gap-4">
            <img
              src={process.env.PUBLIC_URL + "/logo.png"}
              alt="Logo"
              className="w-20 object-contain"
              onError={(e) => {
                e.target.onerror = null;
                e.target.style.display = "none";
              }}
            />
            <div>
              <h1 className="m-0 text-2xl font-bold text-white md:text-3xl">Plan your PR journey</h1>
              <p className="m-0 mt-1 text-sm text-white/80">
                Tell us where you are now. Our team will map the pathways that fit you and get in touch.
              </p>
            </div>
          </div>
        </header>

        {submitted ? (
          <div className="flex flex-col items-center px-8 py-20 text-center">
            <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-emerald-100">
              <svg className="h-10 w-10 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="m-0 text-2xl font-bold text-slate-900">Enquiry received</h2>
            <p className="m-0 mt-2 max-w-md text-slate-600">
              Thanks, {form.name.split(" ")[0] || "there"}. A migration advisor will review your details and contact
              you to plan your next steps.
            </p>
            <p className="m-0 mt-2 text-sm text-slate-400">You can close this tab now.</p>
          </div>
        ) : (
          <>
            <div className="border-b border-slate-100 px-5 pb-4 pt-5 md:px-8">
              <div className="mb-2 flex items-center justify-between text-xs font-semibold text-slate-500">
                <span>
                  Step {stepIndex + 1} of {PR_ENQUIRY_STEPS.length} · {step.label}
                </span>
                <span>{progress}%</span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-teal-500 to-emerald-500 transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <ol className="m-0 mt-3 hidden list-none gap-1 p-0 sm:flex">
                {PR_ENQUIRY_STEPS.map((item, index) => (
                  <li
                    key={item.id}
                    className={`flex-1 text-center text-[11px] font-medium ${
                      index === stepIndex ? "text-teal-700" : index < stepIndex ? "text-slate-500" : "text-slate-300"
                    }`}
                  >
                    {item.label}
                  </li>
                ))}
              </ol>
            </div>

            <form
              className="space-y-8 px-5 py-6 md:px-8"
              onSubmit={(event) => {
                event.preventDefault();
                next();
              }}
            >
              {step.id === "about" ? (
                <>
                  <Section title="Your details">
                    <Grid>
                      <Text label="Full name" required value={form.name} onChange={set("name")} />
                      <Text label="Email" type="email" required value={form.email} onChange={set("email")} />
                      <Text label="Phone / WhatsApp" required value={form.number} onChange={set("number")} />
                      <Text label="Date of birth" type="date" value={form.dob} onChange={set("dob")} helperText="Age affects points" />
                      <Text label="Nationality" value={form.nationality} onChange={set("nationality")} />
                      <Text label="Current city" value={form.currentCity} onChange={set("currentCity")} />
                    </Grid>
                  </Section>
                  <Choices
                    label="Are you in Australia now?"
                    required
                    options={YES_NO}
                    value={form.inAustralia}
                    onChange={set("inAustralia")}
                  />
                  <Choices
                    label="Relationship status"
                    options={MARITAL_STATUSES}
                    value={form.maritalStatus}
                    onChange={set("maritalStatus")}
                  />
                </>
              ) : null}

              {step.id === "visa" ? (
                <>
                  <Choices label="Current visa" required options={VISA_TYPES} value={form.visaStatus} onChange={set("visaStatus")} />
                  <Grid>
                    <Text
                      label="Visa valid until"
                      type="date"
                      required={form.inAustralia === "Yes"}
                      value={form.visaExpiry}
                      onChange={set("visaExpiry")}
                    />
                  </Grid>
                  <Section title="Study" hint="Your current or most recent course.">
                    <Grid>
                      <Text label="Course name" value={form.currentCourse} onChange={set("currentCourse")} placeholder="e.g. Diploma of Hospitality" />
                      <Text label="College / university" value={form.currentCourseProvider} onChange={set("currentCourseProvider")} />
                      <Text label="Course ends" type="date" value={form.courseEndDate} onChange={set("courseEndDate")} />
                     
                    </Grid>
                    <Choices label="Course level" options={COURSE_LEVELS} value={form.courseLevel} onChange={set("courseLevel")} />
                    <Choices
                      label="Have you completed 2+ years of study in Australia?"
                      options={YES_NO}
                      value={form.twoYearsAustralianStudy}
                      onChange={set("twoYearsAustralianStudy")}
                    />
                    <Choices
                      label="Did you study in a regional area?"
                      options={YES_NO}
                      value={form.regionalStudy}
                      onChange={set("regionalStudy")}
                    />
                  </Section>
                </>
              ) : null}

              {step.id === "work" ? (
                <>
                  <ExperienceList
                    title="Work in Australia (onshore)"
                    hint="Paid jobs in Australia, including part-time and casual."
                    entries={form.onshore}
                    onChange={set("onshore")}
                    showHours
                  />
                  <ExperienceList
                    title="Work overseas (offshore)"
                    hint="Jobs outside Australia, especially in your field."
                    entries={form.offshore}
                    onChange={set("offshore")}
                  />
                </>
              ) : null}

              {step.id === "skills" ? (
                <>
                  <Section title="English">
                    <Choices label="English test" options={ENGLISH_TESTS} value={form.englishTest} onChange={set("englishTest")} />
                    {form.englishTest && form.englishTest !== "Not taken yet" ? (
                      <Grid>
                        <Text label="Overall score" value={form.englishScore} onChange={set("englishScore")} placeholder="e.g. 7.0 or 65" /> <Text label="Reading score" value={form.readingScore} onChange={set("readingScore")} placeholder="e.g. 7.0 or 65" />
                        <Text label="Listening score" value={form.listeningScore} onChange={set("listeningScore")} placeholder="e.g. 7.0 or 65" />
                        <Text label="Speaking score" value={form.speakingScore} onChange={set("speakingScore")} placeholder="e.g. 7.0 or 65" />
                        <Text label="Writing score" value={form.writingScore} onChange={set("writingScore")} placeholder="e.g. 7.0 or 65" />
                        <Text label="Test date" type="date" value={form.englishTestDate} onChange={set("englishTestDate")} />
                      </Grid>
                    ) : null}
                  </Section>
                  <Section title="Occupation & skills">
                    <Grid>
                      <Text
                        label="Occupation you'd nominate"
                        value={form.occupation}
                        onChange={set("occupation")}
                        placeholder="e.g. Cook, Software Engineer"
                      />
                    </Grid>
                    <Choices label="Skills assessment" options={SKILLS_ASSESSMENT} value={form.skillsAssessment} onChange={set("skillsAssessment")} />
                    <Choices label="Completed a Professional Year?" options={YES_NO} value={form.professionalYear} onChange={set("professionalYear")} />
                    <Choices label="Passed NAATI CCL?" options={YES_NO} value={form.naatiCcl} onChange={set("naatiCcl")} />
                  </Section>
                </>
              ) : null}

              {step.id === "goals" ? (
                <>
                  <Section title="Your plan">
                    <Text
                      label="What are your plans for the next few years?"
                      multiline
                      value={form.futurePlan}
                      onChange={set("futurePlan")}
                      placeholder="e.g. Finish my diploma, get a skills assessment, apply for 190 in Victoria"
                    />
                  </Section>
                  <Choices label="PR pathways you're interested in" required multiple options={PR_PATHWAYS} value={form.pathways} onChange={set("pathways")} />
                  <Choices label="Preferred states" multiple options={STATES} value={form.preferredStates} onChange={set("preferredStates")} />
                  <Choices label="Open to living in a regional area?" options={["Yes", "No", "Maybe"]} value={form.willingRegional} onChange={set("willingRegional")} />
                  <Choices label="When would you like to apply?" options={TIMELINES} value={form.timeline} onChange={set("timeline")} />
                  <Choices label="Will your partner be included?" options={["Yes", "No", "No partner"]} value={form.partnerIncluded} onChange={set("partnerIncluded")} />
                  <Text label="Anything else we should know? or Any immediate actions to be taken?" multiline value={form.comments} onChange={set("comments")} />

                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                    <p className="m-0 mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Your summary</p>
                    <dl className="m-0 divide-y divide-slate-200/70">
                      <ReviewRow label="Name" value={form.name} />
                      <ReviewRow label="Visa" value={[form.visaStatus, form.visaExpiry && `until ${form.visaExpiry}`].filter(Boolean).join(" ")} />
                      <ReviewRow label="Course" value={[form.currentCourse, form.currentCourseProvider].filter(Boolean).join(" · ")} />
                      <ReviewRow label="Onshore work" value={form.onshore.length ? onshoreTotal : ""} />
                      <ReviewRow label="Offshore work" value={form.offshore.length ? offshoreTotal : ""} />
                      <ReviewRow label="English" value={[form.englishTest, form.englishScore].filter(Boolean).join(" ")} />
                      <ReviewRow label="Occupation" value={form.occupation} />
                    </dl>
                  </div>

                  <FormControlLabel
                    control={<Checkbox checked={form.consent} onChange={(event) => set("consent")(event.target.checked)} />}
                    label={
                      <span className="text-sm text-slate-700">
                        I agree to be contacted about my enquiry, and that my details are used to assess my PR options.
                      </span>
                    }
                  />
                </>
              ) : null}

              {problem ? <Alert severity="error">{problem}</Alert> : null}

              <div className="flex flex-col-reverse gap-3 border-t border-slate-100 pt-5 sm:flex-row sm:justify-between">
                <Button onClick={back} disabled={stepIndex === 0 || submit.isLoading}>
                  Back
                </Button>
                <Button type="submit" variant="contained" disableElevation disabled={submit.isLoading} sx={{ minWidth: 160 }}>
                  {isLast ? (submit.isLoading ? "Sending…" : "Send enquiry") : "Continue"}
                </Button>
              </div>
              <p className="m-0 text-center text-xs text-slate-400">Your answers are kept if you refresh this tab, and cleared when you close it.</p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
